import * as React from 'react';
import { styled, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Drawer from '@mui/material/Drawer';
import MenuIcon from '@mui/icons-material/Menu';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ColorModeIconDropdown from '../../shared-theme/ColorModeIconDropdown';
import LogoProducts from './LogoProducts';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { useThemeContext } from '../../../contexts/ThemeContext';

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
  borderRadius: `calc(${theme.shape.borderRadius}px + 8px)`,
  backdropFilter: 'blur(24px)',
  border: '1px solid',
  borderColor: theme.palette.divider,
  backgroundColor: alpha(theme.palette.background.default, 0.4),
  boxShadow: theme.shadows[1],
  padding: '8px 12px',
}));

export default function HeaderProducts() {
  const [open, setOpen] = React.useState(false);
  const { mode, toggleColorMode } = useThemeContext();

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  return (
    <AppBar
      position="fixed"
      enableColorOnDark
      sx={{
        boxShadow: 0,
        bgcolor: 'transparent',
        backgroundImage: 'none',
        mt: 'calc(var(--template-frame-height, 0px) + 28px)',
      }}
    >
      <Container maxWidth="lg">
        <StyledToolbar variant="dense" disableGutters>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', px: 0 }}>
            <LogoProducts />
            <Box sx={{ display: { xs: 'none', md: 'flex' }, ml: 22, gap: 8, alignItems: 'center' }}>

              <Button
                variant="text"
                color="info"
                size="small"
                component="a"
                href="#hardwareproducts"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  lineHeight: 1.2,
                  color: 'text.primary',
                }}
              >
                <span>Computação</span>
                <span>&</span>
                <span>Hardware</span>
              </Button>

              <Button
                variant="text"
                color="info"
                size="small"
                component="a"
                href="#gadgets"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  lineHeight: 1.2,
                  color: 'text.primary',
                }}
              >
                <span>Periféricos</span>
                <span>e</span>
                <span>Acessórios</span>
              </Button>

              <Button
                variant="text"
                color="info"
                size="small"
                component="a"
                href="#smartdevicesproducts"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  lineHeight: 1.2,
                  color: 'text.primary',
                }}
              >
                <span>Dispositivos</span>
                <span>Inteligentes</span>
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              gap: 2,
              alignItems: 'center',
            }}
          >
            <IconButton
              color="inherit"
              size="small"
              component="a"
              href="/favoritos"
              rel="noopener noreferrer"
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <FavoriteIcon />
            </IconButton>
            <Button
              variant="text"
              color="info"
              size="small"
              component="a"
              href="/meu-carrinho"
              rel="noopener noreferrer"
              sx={{
                color: 'text.primary',
              }}
            >
              Carrinho
            </Button>
            <Button
              color="primary"
              variant="contained"
              size="small"
              component="a"
              href="/login"
              rel="noopener noreferrer"
            >
              Entrar
            </Button>
            <ColorModeIconDropdown mode={mode} toggleColorMode={toggleColorMode} />
          </Box>

          {/* MENU MOBILE */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1 }}>
            <ColorModeIconDropdown size="medium" mode={mode} toggleColorMode={toggleColorMode} />
            <IconButton aria-label="Menu button" onClick={toggleDrawer(true)}>
              <MenuIcon />
            </IconButton>

            <Drawer
              anchor="top"
              open={open}
              onClose={toggleDrawer(false)}
              PaperProps={{
                sx: { top: 'var(--template-frame-height, 0px)' },
              }}
            >
              <Box sx={{ p: 2, backgroundColor: 'background.default' }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton onClick={toggleDrawer(false)}>
                    <CloseRoundedIcon />
                  </IconButton>
                </Box>

                <MenuItem
                  component="a"
                  href="#hardwareproducts"
                  onClick={toggleDrawer(false)}
                >
                  Computação & Hardware
                </MenuItem>
                <MenuItem
                  component="a"
                  href="#gadgets"
                  onClick={toggleDrawer(false)}
                >
                  Periféricos e Acessórios
                </MenuItem>
                <MenuItem
                  component="a"
                  href="#smartdevicesproducts"
                  onClick={toggleDrawer(false)}
                >
                  Dispositivos Inteligentes
                </MenuItem>
                <Divider sx={{ my: 3 }} />

                <IconButton
                  color="inherit"
                  size="small"
                  component="a"
                  href="/favoritos"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    },
                    display: 'flex',
                    alignItems: "center",
                    justifyContent: 'center'
                  }}
                >
                  <FavoriteIcon />
                </IconButton>

                <MenuItem
                  component="a"
                  href="/login"
                  rel="noopener noreferrer"
                  sx={{ p: 0 }}
                >
                  <Button color="primary" variant="contained" fullWidth>
                    Entrar
                  </Button>
                </MenuItem>
                <MenuItem
                  component="a"
                  href="/meu-carrinho"
                  onClick={toggleDrawer(false)}
                  sx={{ p: 0 }}
                >
                  <Button color="primary" variant="outlined" fullWidth>
                    Carrinho
                  </Button>
                </MenuItem>
              </Box>
            </Drawer>
          </Box>
        </StyledToolbar>
      </Container>
    </AppBar>
  );
}